import React, { useState, useEffect, useCallback, useRef } from 'react';
import deviceDataService from '../../services/deviceDataService';
import { fetchFleetHealth, fetchFleetWidespreadIssues } from '../api/restapi';
import '../assets/InsightsPatterns.css';

const DURATIONS = [
    { key: '1d', label: 'D' },
    { key: '7d', label: 'W' },
    { key: '1mo', label: 'M' },
];

const SEV_OF = { CRITICAL: 'critical', DEGRADED: 'warning', STABLE: 'info' };
const SEV_ICON = { critical: '🔴', warning: '🟠', info: '🔵' };
const SEV_BADGE = { critical: 'badge-red', warning: 'badge-amber', info: 'badge-blue' };
const scoreBadge = (s) => s < 0.45 ? 'badge-red' : s < 0.65 ? 'badge-amber' : 'badge-green';

function subdomainScoreLabel(subdomain) {
    const name = (subdomain || '').toUpperCase();
    if (name.includes('THROUGHPUT')) return 'Throughput Score';
    if (name.includes('FLASH') || name.includes('FLASH_STORAGE')) return 'Flash Score';
    if (name.includes('RADIO') || name.includes('RF')) return 'Radio Score';
    if (name.includes('POWER') || name.includes('BATTERY')) return 'Power Score';
    if (name.includes('NETWORK')) return 'Network Score';
    if (name.includes('LATENCY')) return 'Latency Score';
    if (name.includes('STABILITY') || name.includes('CRASH')) return 'Stability Score';
    const titleCase = subdomain.charAt(0).toUpperCase() + subdomain.slice(1).toLowerCase();
    return `${titleCase} Score`;
}

const MAX_ASSESSMENT_RETRIES = 3;

function durationToServiceRange(dur) {
    if (dur === '1d') return '24h';
    if (dur === '1mo') return '30d';
    return dur;
}

function durationToDays(dur) {
    if (dur === '1d') return 1;
    if (dur === '1mo') return 30;
    return 7;
}

function SectionHeader({ icon, iconClass, title, sub }) {
    return (
        <div className="sec-hdr">
            <div className={`sec-icon ${iconClass}`}>{icon}</div>
            <h2>{title}</h2>
            {sub && <span className="sec-sub">{sub}</span>}
            <div className="line" />
        </div>
    );
}

function FleetHealthInsightsPatterns({ userInfo, role, onNavigateToDeviceProfile, deepLink, onDeepLinkConsumed, widespreadIssues }) {
    const [selectedDuration, setSelectedDuration] = useState('7d');
    const [severityFilter, setSeverityFilter] = useState('all');
    const [subdomainFilter, setSubdomainFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [classData, setClassData] = useState(null);
    const [metaData, setMetaData] = useState(null);
    const [fleetHealthData, setFleetHealthData] = useState(null);
    const [localIssues, setLocalIssues] = useState(widespreadIssues?.issues ?? []);

    // Per-subdomain AI assessment cache: { [subdomain]: { data, loading, error, warming } }
    const [assessments, setAssessments] = useState({});
    const apigeeTokenRef = useRef(null);
    const assessmentRetryRef = useRef({});

    // ── Fetch APIGEE token (cached in ref) ────────────────────────────────────
    const ensureApigeeToken = useCallback(async () => {
        if (apigeeTokenRef.current) return apigeeTokenRef.current;
        try {
            const tokenUrl = (process.env.REACT_APP_WBC_API_BASE_URL || '') + '/api/v1/apigee/token';
            const res = await fetch(tokenUrl);
            if (res.ok) {
                const d = await res.json();
                apigeeTokenRef.current = d.access_token || null;
            }
        } catch (e) {
            console.warn('Failed to fetch APIGEE token for subdomain assessment:', e.message);
        }
        return apigeeTokenRef.current;
    }, []);

    // ── Fetch subdomain AI assessment ─────────────────────────────────────────
    const fetchAssessment = useCallback(async (subdomain, days, attempt = 0) => {
        const base = process.env.REACT_APP_WBC_AI_INSIGHTS_URL;
        const ver = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2';
        const url = `${base}/api/${ver}/insights/fleet/subdomain-assessment?subdomain=${subdomain.toLowerCase()}&days=${days}`;

        setAssessments(prev => ({
            ...prev,
            [subdomain]: { ...prev[subdomain], loading: true, error: null },
        }));

        try {
            const token = await ensureApigeeToken();
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await fetch(url, { headers });

            if (res.status === 200) {
                const data = await res.json();
                assessmentRetryRef.current[subdomain] = 0;
                setAssessments(prev => ({
                    ...prev,
                    [subdomain]: { data, loading: false, error: null },
                }));
            } else if (res.status === 202) {
                const body = await res.json();
                if (attempt < MAX_ASSESSMENT_RETRIES) {
                    const retryAfter = (body.retry_after || 30) * 1000;
                    setAssessments(prev => ({
                        ...prev,
                        [subdomain]: {
                            ...prev[subdomain],
                            loading: true,
                            error: null,
                            warming: `Generating insight… (attempt ${attempt + 1}/${MAX_ASSESSMENT_RETRIES})`,
                        },
                    }));
                    setTimeout(() => fetchAssessment(subdomain, days, attempt + 1), retryAfter);
                } else {
                    setAssessments(prev => ({
                        ...prev,
                        [subdomain]: { data: null, loading: false, error: 'Insight generation timed out. Try again later.' },
                    }));
                }
            } else if (res.status === 502 || res.status === 500 || res.status === 404) {
                setAssessments(prev => ({
                    ...prev,
                    [subdomain]: { data: null, loading: false, error: 'no_insight' },
                }));
            } else {
                setAssessments(prev => ({
                    ...prev,
                    [subdomain]: { data: null, loading: false, error: `Unexpected error (HTTP ${res.status}).` },
                }));
            }
        } catch (err) {
            setAssessments(prev => ({
                ...prev,
                [subdomain]: { data: null, loading: false, error: 'Network error. Check your connection.' },
            }));
        }
    }, [ensureApigeeToken]);

    // ── fetchAssessment ref so fetchAll can call it without circular dependency ─
    const fetchAssessmentRef = useRef(fetchAssessment);
    useEffect(() => { fetchAssessmentRef.current = fetchAssessment; }, [fetchAssessment]);

    // ── Fetch all base data + widespread issues for a given duration ──────────
    const fetchAll = useCallback(async (dur, isInitial) => {
        if (isInitial) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }
        setError(null);

        const days = durationToDays(dur);

        try {
            const metaPromise = isInitial
                ? deviceDataService.getFleetSubdomainMetadata()
                : Promise.resolve(null);

            const [cls, meta, fleetHealth, issuesData] = await Promise.all([
                deviceDataService.getFleetDeviceClassification(durationToServiceRange(dur)),
                metaPromise,
                fetchFleetHealth(days),
                fetchFleetWidespreadIssues(days),
            ]);

            if (cls?.cacheError) {
                setError(cls.message || 'Error loading data');
            } else {
                setClassData(cls);
                if (isInitial && meta && !meta.cacheError) setMetaData(meta);
                if (!fleetHealth?.errorMessage) setFleetHealthData(fleetHealth);
                if (!issuesData?.errorMessage) {
                    const newIssues = issuesData?.issues ?? [];
                    setLocalIssues(newIssues);
                    if (newIssues.length) {
                        setAssessments({});
                        assessmentRetryRef.current = {};
                        newIssues.forEach(issue => fetchAssessmentRef.current(issue.subdomain, days, 0));
                    }
                }
            }
        } catch (err) {
            setError('Unexpected error loading Insights & Patterns data.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchAll(selectedDuration, true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Seed local issues + assessments from prop (7d pre-fetch by FleetHealthTabs) ─
    useEffect(() => {
        const issues = widespreadIssues?.issues;
        if (!issues?.length) return;
        setLocalIssues(issues);
        const days = durationToDays(selectedDuration);
        setAssessments({});
        assessmentRetryRef.current = {};
        issues.forEach(issue => fetchAssessment(issue.subdomain, days, 0));
    }, [widespreadIssues]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Deep-link from Overview banner click ──────────────────────────────────
    useEffect(() => {
        if (!deepLink) return;
        setExpandedId(null);
        setSubdomainFilter(deepLink.subdomain ?? 'all');
        if (deepLink.duration && deepLink.duration !== selectedDuration) {
            setSelectedDuration(deepLink.duration);
            fetchAll(deepLink.duration, false);
        }
        onDeepLinkConsumed && onDeepLinkConsumed();
    }, [deepLink]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDurationChange = (dur) => {
        if (dur === selectedDuration) return;
        setSelectedDuration(dur);
        setExpandedId(null);
        fetchAll(dur, false);
    };

    const toggleCluster = (id) => {
        setExpandedId(prev => prev === id ? null : id);
    };

    // ── Derived KPIs from device-classification ──────────────────────────────
    const devices = classData?.devices ?? [];
    const totalDevices = classData?.activeDeviceCount ?? devices.length;
    const degradedCount = devices.filter(d => d.severity === 'DEGRADED').length;
    const criticalCount = devices.filter(d => d.severity === 'CRITICAL').length;
    const fleetScore = fleetHealthData?.fleetScorePct ?? null;

    // ── Filter clusters ───────────────────────────────────────────────────────
    const filteredClusters = localIssues.filter(s => {
        const sev = SEV_OF[s.severity] ?? 'info';
        if (severityFilter !== 'all' && sev !== severityFilter) return false;
        if (subdomainFilter !== 'all' && s.subdomain !== subdomainFilter) return false;
        return true;
    });

    // ── Subdomain metadata for dropdown ──────────────────────────────────────
    const allSubdomains = metaData?.subdomains ?? [];

    // ── Render states ─────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="ip-dashboard">
                <div className="ip-state">
                    <div className="ip-state-icon">⏳</div>
                    <h3>Loading Insights &amp; Patterns…</h3>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ip-dashboard">
                <div className="ip-state">
                    <div className="ip-state-icon">⚠️</div>
                    <h3>Could not load data</h3>
                    <p>{error}</p>
                    <div className="ip-retry-row">
                        <button className="ip-retry-btn" onClick={() => fetchAll(selectedDuration, true)}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ip-dashboard">
            {/* ── Page intro + duration filter ── */}
            <div className="page-intro">
                <div className="duration-bar">
                    <span className="duration-label">Duration:</span>
                    {DURATIONS.map(d => (
                        <span
                            key={d.key}
                            className={`filter-chip${selectedDuration === d.key ? ' active' : ''}${refreshing ? ' disabled' : ''}`}
                            onClick={() => handleDurationChange(d.key)}
                        >
                            {d.label}
                        </span>
                    ))}
                    {refreshing && <span className="assess-spinner" style={{ marginLeft: 6 }} />}
                </div>
            </div>

            {/* ── Anomaly Overview ── */}
            <SectionHeader icon="🔎" iconClass="blue" title="Anomaly Overview" />
            <div className="grid-4">
                <div className="kpi-card">
                    <div className="kpi-label">Active Devices</div>
                    <div className="kpi-value">{totalDevices.toLocaleString()}</div>
                </div>
                <div className="kpi-card kpi-amber">
                    <div className="kpi-label">Devices At-Risk</div>
                    <div className="kpi-value">{degradedCount.toLocaleString()}</div>
                </div>
                <div className="kpi-card kpi-red">
                    <div className="kpi-label">Critical Devices</div>
                    <div className="kpi-value">{criticalCount.toLocaleString()}</div>
                </div>
                <div className="kpi-card kpi-green">
                    <div className="kpi-label">Fleet Health Score</div>
                    <div className="kpi-value">{fleetScore !== null ? `${fleetScore}/100` : '—'}</div>
                </div>
            </div>

            {/* ── AI Insights ── */}
            <SectionHeader icon="🤖" iconClass="purple" title="AI Insights" />

            {/* Filter bar */}
            <div className="filter-bar">
                {[
                    { key: 'all',      label: 'All Patterns' },
                    { key: 'critical', label: '🔴 Critical' },
                    { key: 'warning',  label: '🟠 Warning' },
                    { key: 'info',     label: '🔵 Info' },
                ].map(chip => (
                    <span
                        key={chip.key}
                        className={`filter-chip${severityFilter === chip.key ? ' active' : ''}`}
                        onClick={() => { setSeverityFilter(chip.key); setExpandedId(null); }}
                    >
                        {chip.label}
                    </span>
                ))}

                {allSubdomains.length > 0 && (
                    <select
                        className="filter-select"
                        value={subdomainFilter}
                        onChange={e => { setSubdomainFilter(e.target.value); setExpandedId(null); }}
                    >
                        <option value="all">All Areas</option>
                        {allSubdomains.map(s => (
                            <option key={s.subdomain} value={s.subdomain}>{s.subdomain}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* Cluster list */}
            {filteredClusters.length === 0 ? (
                <div className="ip-empty">No patterns match the current filters.</div>
            ) : (
                filteredClusters.map(cluster => {
                    const sev = SEV_OF[cluster.severity] ?? 'info';
                    const isOpen = expandedId === cluster.subdomain;
                    const assessment = assessments[cluster.subdomain];

                    const headerDesc = assessment?.data?.insights?.summary
                        ? assessment.data.insights.summary
                        : `Affecting ${cluster.affectedDeviceCount} device${cluster.affectedDeviceCount !== 1 ? 's' : ''}`;

                    return (
                        <div key={cluster.subdomain} className={`cluster-card${isOpen ? ' open' : ''}`}>
                            <div className="cluster-hdr" onClick={() => toggleCluster(cluster.subdomain)}>
                                <div className={`cluster-sev ${sev}`}>{SEV_ICON[sev]}</div>
                                <div style={{ flex: 1 }}>
                                    <div className="cluster-title">
                                        {cluster.subdomain}
                                        <span className={`badge ${SEV_BADGE[sev]}`}>{cluster.severity}</span>
                                    </div>
                                    <div className="cluster-desc">{headerDesc}</div>
                                </div>
                                <div className="cluster-expand-icon">▼</div>
                            </div>

                            <div className={`cluster-body${isOpen ? ' open' : ''}`}>
                                {assessment?.loading ? (
                                    <div className="assess-loading">
                                        <span className="assess-spinner" />
                                        <span>{assessment.warming || 'Loading AI assessment…'}</span>
                                    </div>
                                ) : assessment?.error === 'no_insight' ? (
                                    <div className="action-box">
                                        AI assessment not yet available for this subdomain.
                                    </div>
                                ) : assessment?.error ? (
                                    <div className="action-box">
                                        <b>Error:</b> {assessment.error}
                                        <button
                                            className="assess-retry-btn"
                                            onClick={() => fetchAssessment(cluster.subdomain, durationToDays(selectedDuration), 0)}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : assessment?.data ? (
                                    <>
                                        {/* Cache banner */}
                                        {assessment.data.cache_metadata && (
                                            <div className="assess-cache-banner">
                                                <span className={`assess-cache-dot ${assessment.data.cache_metadata.is_stale ? 'stale' : 'ready'}`} />
                                                <span>Last updated: <b>{(() => {
                                                    const diff = Date.now() - new Date(assessment.data.cache_metadata.cached_at).getTime();
                                                    const m = Math.floor(diff / 60000);
                                                    const h = Math.floor(diff / 3600000);
                                                    if (m < 1) return 'just now';
                                                    if (m < 60) return `${m}m ago`;
                                                    return `${h}h ago`;
                                                })()}</b></span>
                                            </div>
                                        )}

                                        {/* AI Narrative */}
                                        <div className="assess-narrative">
                                            {assessment.data.insights?.what_it_means && (
                                                <div className="assess-section">
                                                    <div className="assess-section-label">ℹ️ What It Means</div>
                                                    <div className="assess-section-body">{assessment.data.insights.what_it_means}</div>
                                                </div>
                                            )}

                                            {assessment.data.insights?.why_flagged && (
                                                <div className="assess-section">
                                                    <div className="assess-section-label">⚠️ Why Flagged</div>
                                                    <div className="assess-section-body">{assessment.data.insights.why_flagged}</div>
                                                </div>
                                            )}

                                            {assessment.data.insights?.recommended_actions?.length > 0 && (
                                                <div className="assess-section">
                                                    <div className="assess-section-label">✅ Recommended Actions</div>
                                                    <ol className="assess-actions-list">
                                                        {assessment.data.insights.recommended_actions.map((action, i) => (
                                                            <li key={i}>{action}</li>
                                                        ))}
                                                    </ol>
                                                </div>
                                            )}
                                        </div>

                                        {/* Contributing devices */}
                                        {assessment.data.context?.contributingDevices?.length > 0 && (
                                            <div className="assess-metric-section">
                                                <div className="assess-subsection-title">
                                                    👥 Impacted Devices
                                                    <span className="assess-device-count">
                                                        {assessment.data.context?.affectedDeviceCount} of {assessment.data.context?.activeDeviceCount} active
                                                    </span>
                                                </div>
                                                <div className="boot-kpi-table-container" style={{ maxHeight: 280, overflowY: 'auto' }}>
                                                    <div className="boot-kpi-table-wrapper">
                                                        <table className="boot-kpi-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                            <colgroup>
                                                                <col style={{ width: '55%' }} />
                                                                <col style={{ width: '20%' }} />
                                                                <col style={{ width: '15%' }} />
                                                                <col style={{ width: '10%' }} />
                                                            </colgroup>
                                                            <thead>
                                                                <tr>
                                                                    <th className="boot-kpi-table-header-kpi">System ID</th>
                                                                    <th className="boot-kpi-table-header">{subdomainScoreLabel(cluster.subdomain)}</th>
                                                                    <th className="boot-kpi-table-header">Severity</th>
                                                                    <th className="boot-kpi-table-header"></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {assessment.data.context?.contributingDevices.map((dev, idx) => {
                                                                    const devSev = SEV_OF[dev.severity] ?? 'info';
                                                                    return (
                                                                        <tr key={dev.systemId}>
                                                                            <td className="boot-kpi-table-cell-kpi" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={dev.systemId}>{dev.systemId}</td>
                                                                            <td className="boot-kpi-table-cell">
                                                                                <span className={`badge ${scoreBadge(dev.averageScore)}`}>
                                                                                    {(dev.averageScore * 100).toFixed(1)}
                                                                                </span>
                                                                            </td>
                                                                            <td className="boot-kpi-table-cell">
                                                                                <span className={`badge ${SEV_BADGE[devSev]}`}>
                                                                                    {dev.severity}
                                                                                </span>
                                                                            </td>
                                                                            <td className="boot-kpi-table-cell" style={{ whiteSpace: 'nowrap' }}>
                                                                                <span
                                                                                    className="dlink"
                                                                                    style={{ cursor: 'pointer', color: 'var(--ip-primary)' }}
                                                                                    onClick={() => onNavigateToDeviceProfile && onNavigateToDeviceProfile(dev.systemId)}
                                                                                >
                                                                                    Deep Dive →
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : null}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
}

export default FleetHealthInsightsPatterns;
